<template>
  <MarketplaceCard
    v-model="localConfig.enabled"
    :hide-toggle="true"
    :logo-path="QuickbooksLogo"
    logo-alt="QuickBooks"
    description="Connect to QuickBooks Online to make accounting even easier."
    :error-text="errorSummary"
    learn-more-link="https://quickbooks.intuit.com/"
    cy-selector="marketplaceQuickBooks"
  >
    <template #configure>
      <div>
        <div data-cy="setup-information-link" class="link" @click="openModal">
          Set Up Information
        </div>
      </div>
    </template>
    <template #modal>
      <Modal
        v-if="showConfigModal"
        title="Set Up Information - QuickBooks"
        class-name="quickbooks-config-modal"
        :no-submit="true"
        @hide-modal="handleModalClosed"
      >
        <div class="d-flex flex-column full-width">
          <QuickbooksSetup />
        </div>
        <template #footer>
          <div
            v-if="canAddMoreCompanies && !inOAuthFlow"
            class="modal-actions d-flex align-items-end flex-wrap"
            @click="linkCompany"
          >
            <component
              :is="connectButton"
              class="connect-button m-4"
              @mouseover="connectButton = ConnectToQBButtonHover"
              @mouseout="connectButton = ConnectToQBButtonDefault"
            />
          </div>
        </template>
      </Modal>
    </template>
  </MarketplaceCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import Modal from "@/components/Modal.vue";
import QuickbooksLogo from "@/images/quickbooks_logo.png";
import { useQuickbooksConfig } from "@/Marketplace/Quickbooks/useQuickbooksConfig";
import MarketplaceCard from "./MarketplaceCard.vue";
import ConnectToQBButtonDefault from "./Quickbooks/C2QB_green_btn_tall_default.svg?component";
import ConnectToQBButtonHover from "./Quickbooks/C2QB_green_btn_tall_hover.svg?component";
import QuickbooksSetup from "./Quickbooks/QuickbooksSetup.vue";

const {
  fetchQuickbooksCompanies,
  quickbooksCompanies,
  inOAuthFlow,
  multipleQboFeatureFlag,
  linkCompany
} = useQuickbooksConfig(true);

const canAddMoreCompanies = computed(() => {
  return multipleQbo.value || quickbooksCompanies.value.length === 0;
});

const localConfig = ref({
  enabled: false
});
const showConfigModal = ref(false);
const multipleQbo = ref(false);
const connectButton = ref(ConnectToQBButtonDefault);

const companyErrors = computed(() => {
  return quickbooksCompanies.value
    .filter((company) => company.errors.length > 0)
    .map((company) => company.errors[0])
    .filter((reason) => !!reason);
});

const errorSummary = computed(() => {
  return companyErrors.value.length > 1
    ? `${companyErrors.value.length} companies require attention!`
    : companyErrors.value[0];
});

function openModal() {
  showConfigModal.value = true;
}

function handleModalClosed() {
  showConfigModal.value = false;
}

onMounted(async () => {
  try {
    await fetchQuickbooksCompanies();
    multipleQbo.value = await multipleQboFeatureFlag();
  } catch (e) {
    // Do nothing
  }
});

defineExpose({
  openModal
});
</script>

<style lang="scss">
.modal-actions {
  justify-content: right;
}

.connect-button {
  cursor: pointer;
}
</style>
